import { designVariables } from "@studio3t/design-variables";
import {
  ButtonMediumPrimaryLeadingIcon,
  ButtonMediumSecondaryLeadingIcon,
  Icon,
  IconSVG,
  SVGColorKey,
} from "@studio3t/generic-components";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import {
  Group,
  Panel,
  type PanelImperativeHandle,
  useDefaultLayout,
} from "react-resizable-panels";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { AiChatSidebar, useAiChatContext } from "@/components/ai/chat";
import { StudioActionsContext } from "@/components/ai/chat/StudioActionsContext";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { useAnyModalOpen } from "@/components/ModalChrome";
import { ColumnSeparator } from "@/components/PanelSeparators";
import { useErrorToast } from "@/components/toast/useErrorToast";
import { userStorage } from "@/utils/userStorage";
import { deployBlockers, deployBlockersIntro } from "./deployBlockers";
import { ExplorerProvider, useExplorer } from "./ExplorerContext";
import { NewExplorer } from "./NewExplorer";
import {
  type NameModalKind,
  type PipelineActionState,
  resolveActionError,
  type SaveHold,
  useDraftSave,
  useGoToProblem,
  usePipelineActionState,
} from "./pipelineActionsState";
import { PipelineStagingProvider, usePipelineStaging } from "./pipelineStaging";
import {
  DEPLOY_PIPELINE_MODAL,
  DUPLICATE_PIPELINE_MODAL,
  duplicateName,
  SAVE_DRAFT_MODAL,
  SaveWorkspaceModal,
} from "./SaveWorkspaceModal";
import { StudioPanels } from "./StudioPanels";
import {
  type OpenPipeline,
  StudioPipelineProvider,
  type StudioPipelineValue,
  useStudioPipeline,
} from "./StudioPipelineProvider";
import { StudioCommands } from "./studioCommands";
import { DeployButton } from "./UnresolvedDeployButton";
import { listTabNames, type UnsavedTab, unsavedWorkTabs } from "./unsavedTabs";
import { useAiHelperProps } from "./useAiHelperProps";
import { useScriptRun } from "./useScriptRun";
import { useStudioActions } from "./useStudioActions";
import { WorkspaceControls } from "./WorkspaceControls";

const H2 = designVariables.type.heading.h2;
const SEMANTIC = designVariables.ui.semantic;
const CARDS = designVariables.ui.cards;

// Figma: new Studio screen (13349-30157) — column of title row, studio topbar
// and the content row (Explorer | Main | AI Helper). Gaps mirror the Figma
// frame nesting: spacingl between title and topbar, spacings above content.
const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 48px - 40px);
  gap: ${SEMANTIC.spacingl}px;
`;

// Figma: Page/title (14397-86283) — one row: the "Studio" heading on the
// left, the workspace actions in the middle, deploy on the right (the old
// separate topbar row merged in).
const PageTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(props) => props.theme.semanticAppBackground};
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
`;

const Title = styled.h1`
  font-size: ${H2.fontSize}px;
  font-weight: ${H2.fontWeight};
  line-height: 1.2;
  margin: 0;
  color: ${(props) => props.theme.semanticTextHeading};
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
`;

const StudioSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

// The content row (TTTL-290): Explorer | workbench | AI Helper as a
// react-resizable-panels group, the same package as the workbench's own
// splits. The split is remembered per user under the group id.
const RowGroup = styled(Group)`
  width: 100%;
  flex: 1;
  min-height: 0;
`;

// Side panel width band (Figma 16142-214734 at the narrowest, 16142-217727
// at the widest). Pixels, not a share of the row: a side panel keeps its
// width when the window resizes, and double-clicking its gutter is the
// package's own reset to defaultSize — back to the 330 minimum.
const SIDE_PANEL = { initial: "330px", min: "330px", max: "520px" } as const;

// The collapsed strips' width (CollapsedPanel in NewExplorer and
// AiCollapsedPanel below); a collapsible panel snaps to it once dragged
// under the band.
const SIDE_PANEL_COLLAPSED_PX = 44;

// The row's panel ids. All three panels stay mounted in every state: the
// package cannot admit a Panel into a live group after mount (its
// registration lands one render after a sibling's effects already ran —
// "Panel constraints not found"), and keying the group to remount it
// instead would remount the workbench editor and the AI chat with it. So
// the view-only row, which has no Explorer, keeps the Explorer panel at a
// 0px collapse with nothing inside (SidePanel's `hidden`) rather than
// dropping it.
const EXPLORER_PANEL_ID = "studio-explorer";
const MAIN_PANEL_ID = "studio-main-column";
const HELPER_PANEL_ID = "studio-ai-helper";

// Small screens (TTTL-186): the workbench never shrinks below the width its
// stage tabs need in one row (the toolbar drops its progress readout
// first) — narrower, the AI Helper would paint over the toolbar and the
// tabs over each other. 520 keeps a 1280px window scroll-free with both
// side panels open at their narrowest; collapsing the Explorer or the
// Helper wins the room back.
const WORKBENCH_MIN_WIDTH = 520;

// The gutter between a side card and the workbench. The workbench's green
// border is the one shared edge (each side card drops its border on that
// side), and the gutter straddles that line with no layout width of its
// own, its grip lighting up over it on hover like the workbench's gutters.
const SideSeparator = styled(ColumnSeparator).attrs<{
  $line?: string;
  $hidden?: boolean;
}>(() => ({
  $line: "transparent",
}))`
  width: 8px;
  margin: 0 -4px;
  display: ${(p) => (p.$hidden ? "none" : "block")};
`;

// Whether a side panel's pixel size is the collapsed strip rather than
// somewhere in the 330–520 band (the round absorbs the package's
// percent → pixel conversion).
function isCollapsedSize(px: number): boolean {
  return Math.round(px) <= SIDE_PANEL_COLLAPSED_PX;
}

// Keeps a side panel's collapse in step with its state. The state drives
// the panel imperatively — the panel stays mounted in either state, v4
// can't re-admit a conditionally mounted Panel into the group layout —
// and a drag or gutter double-click that crosses the collapse threshold
// drives the state back through onResize (SidePanel below). Re-opening
// only resizes a panel that actually starts from collapsed: an open panel
// keeps whatever width the user dragged it to.
//
// The group can refuse an expansion: a row too narrow for this panel's
// minimum next to the workbench's floor and the other strip keeps the
// panel collapsed, and an unchanged size sends no resize notification.
// The state then follows what the panel actually did, instead of the
// open card rendering inside the strip. The same check covers the first
// paint, where a stored "open" the row can't honour lands collapsed.
//
// A hidden panel (the view-only row's Explorer) is a collapse to 0px that
// the state must not learn about: collapse() re-runs whenever `hidden`
// flips, since the collapsed size it snaps to changes with it, and the
// expand on unhide brings back the width the panel had before.
function useSidePanelCollapse(
  collapsed: boolean,
  hidden: boolean,
  setCollapsed: (collapsed: boolean) => void,
) {
  const panelRef = useRef<PanelImperativeHandle>(null);
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (collapsed || hidden) {
      panel.collapse();
      return;
    }
    if (panel.isCollapsed()) panel.expand();
    if (panel.isCollapsed()) setCollapsed(true);
  }, [collapsed, hidden, setCollapsed]);
  return panelRef;
}

// One resizable side panel of the row: the 330–520 band, collapsible to
// its strip. onResize skips the mount call (prev is undefined there) —
// the stored collapse state, not the first measured layout, decides the
// initial state. The package reports sizes from a ResizeObserver, after
// its own render has already shrunk the panel; a plain setState from there
// would paint one frame of the open card squeezed into the strip's width
// (its scrollbar and wrapped text blinking) before the strip replaced it.
// flushSync renders the swap inside the observer callback, before that
// frame paints.
function SidePanel({
  panelId,
  collapsed,
  hidden = false,
  setCollapsed,
  children,
}: {
  panelId: string;
  collapsed: boolean;
  /** Out of the row for now: collapsed to 0px, empty, not resizable. */
  hidden?: boolean;
  setCollapsed: (collapsed: boolean) => void;
  children: ReactNode;
}) {
  const panelRef = useSidePanelCollapse(collapsed, hidden, setCollapsed);
  return (
    <Panel
      id={panelId}
      panelRef={panelRef}
      defaultSize={SIDE_PANEL.initial}
      minSize={SIDE_PANEL.min}
      maxSize={SIDE_PANEL.max}
      collapsible
      collapsedSize={hidden ? 0 : SIDE_PANEL_COLLAPSED_PX}
      disabled={hidden}
      groupResizeBehavior="preserve-pixel-size"
      onResize={(size, _id, prev) => {
        if (prev === undefined || hidden) return;
        flushSync(() => setCollapsed(isCollapsedSize(size.inPixels)));
      }}
    >
      {!hidden && children}
    </Panel>
  );
}

// The deploy modal's list of what is left, under its intro sentence.
const BlockerList = styled.ul`
  margin: ${designVariables.ui.semantic.spacings}px 0 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// The stage tabs holding work a draft save would leave behind — a
// stored draft carries only the pipeline code and the input document,
// so unsaved scripts, schema drafts and dirty tests need their own
// saves in their tabs first (the warning modal below).
function useUnsavedTabs(): UnsavedTab[] {
  const {
    scriptDrafts,
    schemaDrafts,
    tests,
    testDrafts,
    isTestDirty,
    sources,
    transforms,
    sinks,
  } = usePipelineStaging();
  const stageRole = (id: string) => {
    if (sources.some((s) => s.id === id)) return "source" as const;
    if (transforms.some((s) => s.id === id)) return "transform" as const;
    if (sinks.some((s) => s.id === id)) return "sink" as const;
    return null;
  };
  const testIds = new Set([
    ...tests.map((t) => t.id),
    ...Object.keys(testDrafts),
  ]);
  const dirtyTestCount = [...testIds].filter(isTestDirty).length;
  return unsavedWorkTabs({
    scriptDrafts,
    schemaDrafts,
    dirtyTestCount,
    stageRole,
  });
}

// A draft may hold unsaved and unattached work; a deployed pipeline may
// not (TTTL-254). The press lists what is left — by kind, not by file —
// in a modal that only closes; editor errors keep Deploy disabled
// through the Unresolved list instead.
function useDeployGuard(deploy: () => void) {
  const {
    scriptDrafts,
    schemaDrafts,
    schemas,
    transforms,
    sources,
    sinks,
    studioView,
    pipelineExtras,
  } = usePipelineStaging();
  const [blockers, setBlockers] = useState<string[]>([]);
  const guardedDeploy = () => {
    const left = deployBlockers({
      studioView,
      sources,
      sinks,
      transforms,
      extraFields: pipelineExtras.fields,
      scriptDrafts,
      schemaDrafts,
      schemas,
    });
    if (left.length > 0) setBlockers(left);
    else deploy();
  };
  return { blockers, guardedDeploy, dismissBlockers: () => setBlockers([]) };
}

function DeployBlockersModal({
  blockers,
  onClose,
}: {
  blockers: string[];
  onClose: () => void;
}) {
  return (
    <ConfirmDeleteModal
      isOpen={blockers.length > 0}
      title="The pipeline isn't ready to deploy"
      message={
        <>
          {deployBlockersIntro()}
          <BlockerList>
            {blockers.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </BlockerList>
        </>
      }
      confirmType="primary"
      confirmLabel="OK"
      hideCancel
      onConfirm={onClose}
      onCancel={onClose}
    />
  );
}

// The scratch/draft pair (Figma draft 15013-121922): scratch asks for a
// name first, a draft saves over itself.
function DraftActions({
  state,
  onNameModal,
  onSaveDraft,
}: {
  state: PipelineActionState;
  onNameModal: (kind: NameModalKind) => void;
  onSaveDraft: (draftId: string) => void;
}) {
  const { deployOpenDraft } = useStudioPipeline();
  const { openPipeline, pipelineDirty, problems, deployDisabled } = state;
  const goToProblem = useGoToProblem();
  const unsavedTabs = useUnsavedTabs();
  const { setDraftSaveHandler } = usePipelineStaging();
  // The save the warning modal parked — Continue and abandon runs it.
  const [pendingSave, setPendingSave] = useState<(() => void) | null>(null);
  const deploy = () =>
    openPipeline === null ? onNameModal("deploy") : void deployOpenDraft();
  const { blockers, guardedDeploy, dismissBlockers } = useDeployGuard(deploy);
  const save = () =>
    openPipeline === null
      ? onNameModal("save")
      : onSaveDraft(openPipeline.workspace.id);
  const guardedSave = () =>
    unsavedTabs.length > 0 ? setPendingSave(() => save) : save();
  // ⌘S with no row-level edit active lands here — same gate as the
  // toolbar button (a scratch studio with nothing to save just no-ops,
  // like the disabled button).
  useEffect(() => {
    const enabled = openPipeline !== null || pipelineDirty;
    setDraftSaveHandler(enabled ? guardedSave : () => {});
    return () => setDraftSaveHandler(null);
  });
  return (
    <>
      <ButtonMediumSecondaryLeadingIcon
        text={openPipeline === null ? "Save as draft" : "Save"}
        icon={Icon.Save}
        disabled={openPipeline === null && !pipelineDirty}
        onClick={guardedSave}
        testId="studio-save"
      />
      <ConfirmDeleteModal
        isOpen={pendingSave !== null}
        title="Unsaved changes"
        message={unsavedWorkMessage(unsavedTabs)}
        confirmLabel="Continue and abandon"
        cancelLabel="Save your work"
        onConfirm={() => {
          pendingSave?.();
          setPendingSave(null);
        }}
        onCancel={() => setPendingSave(null)}
      />
      <DeployBlockersModal blockers={blockers} onClose={dismissBlockers} />
      <DeployButton
        problems={problems}
        disabled={deployDisabled}
        onDeploy={guardedDeploy}
        onGoTo={goToProblem}
      />
    </>
  );
}

// The studio-wide keyboard pair the old /studio carried: ⌘S saves the
// SELECTED row's edit (or falls back to the guarded draft save), ⌘↵ runs
// the pipeline. Both stand down while a modal is up, and ⌘S always eats
// the browser's save-page dialog on the studio.
function StudioHotkeys() {
  const { requestActiveSave } = usePipelineStaging();
  const { runPipeline } = useScriptRun();
  const modalOpen = useAnyModalOpen();
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isMod = navigator.platform.toUpperCase().includes("MAC")
        ? e.metaKey
        : e.ctrlKey;
      if (!isMod) return;
      if (e.key === "s") {
        e.preventDefault();
        if (!modalOpen) requestActiveSave();
        return;
      }
      if (e.key === "Enter" && !modalOpen) {
        e.preventDefault();
        runPipeline();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [requestActiveSave, runPipeline, modalOpen]);
  return null;
}

// "You've got unsaved changes in Source and Transform. …" — the draft
// won't include them; saving them is manual, in their tabs.
function unsavedWorkMessage(tabs: UnsavedTab[]): string {
  const where = `the ${listTabNames(tabs)} ${tabs.length > 1 ? "tabs" : "tab"}`;
  return `You've got unsaved changes in ${listTabNames(tabs)}. A draft doesn't include them — save your work in ${where} first, or continue and abandon the changes.`;
}

// The deployed pair (Figma 14894-157220): duplicating stands in for
// saving, and Stop/Start stands in for Deploy. Start with local edits in
// the editor first asks: the job runs the DEPLOYED config, not what the
// editor shows — the edits stay local through the run, they just aren't
// in it.
function DeployedActions({
  jobControl,
  running,
  onNameModal,
}: {
  jobControl: NonNullable<StudioPipelineValue["jobControl"]>;
  running: boolean;
  onNameModal: (kind: NameModalKind) => void;
}) {
  const { stopPipeline, startPipeline, openPipeline, openEdited } =
    useStudioPipeline();
  const [confirmStart, setConfirmStart] = useState(false);
  const start = () =>
    openEdited ? setConfirmStart(true) : void startPipeline();
  return (
    <>
      <ButtonMediumSecondaryLeadingIcon
        text="Duplicate as draft"
        icon={Icon.Duplicate}
        onClick={() => onNameModal("duplicate")}
        testId="studio-duplicate"
      />
      {running ? (
        <ButtonMediumPrimaryLeadingIcon
          text="Stop pipeline"
          icon={Icon.Stop}
          disabled={jobControl.stopDisabled}
          onClick={() => void stopPipeline()}
          testId="studio-stop"
        />
      ) : (
        <ButtonMediumPrimaryLeadingIcon
          text="Start pipeline"
          icon={Icon.Run}
          disabled={jobControl.startDisabled}
          onClick={start}
          testId="studio-start"
        />
      )}
      <ConfirmDeleteModal
        isOpen={confirmStart}
        title="Start the deployed pipeline?"
        confirmType="primary"
        confirmLabel="Start pipeline"
        message={`Your edits to "${openPipeline?.workspace.name}" are local and not part of the deployed pipeline — starting runs the deployed version without them. Duplicate it as a draft first if you want to run your changes.`}
        onConfirm={() => {
          setConfirmStart(false);
          void startPipeline();
        }}
        onCancel={() => setConfirmStart(false)}
      />
    </>
  );
}

// The name prompt the actions that create a workspace go through.
function PipelineNameModal({
  nameModal,
  openPipeline,
  onClose,
}: {
  nameModal: NameModalKind;
  openPipeline: OpenPipeline | null;
  onClose: () => void;
}) {
  const { duplicateAsDraft, saveAsDraft, deployOpenDraft } =
    useStudioPipeline();
  return (
    <SaveWorkspaceModal
      isOpen
      onClose={onClose}
      {...(nameModal === "duplicate"
        ? DUPLICATE_PIPELINE_MODAL
        : nameModal === "deploy"
          ? DEPLOY_PIPELINE_MODAL
          : SAVE_DRAFT_MODAL)}
      initialName={
        nameModal === "duplicate" && openPipeline
          ? duplicateName(openPipeline.workspace.name)
          : ""
      }
      onSave={async (name) => {
        if (nameModal === "duplicate") {
          await duplicateAsDraft(name);
          return;
        }
        // Only the SAVE gates the modal. The deploy that follows it
        // reports through actionError (the PipelineActions toast), so a
        // failed deploy leaves the modal closed — the workspace it named
        // does exist by then. The freshly created workspace is passed
        // straight through: the state saveAsDraft set hasn't reached
        // this closure yet.
        const ws = await saveAsDraft(name);
        if (nameModal === "deploy") void deployOpenDraft(ws);
      }}
    />
  );
}

// The header's right group: the lifecycle actions of whatever is open
// (Figma draft 15013-121922 / deployed 14894-157220). Scratch and draft
// share the Save + Deploy pair — scratch asks for a name first, a draft
// saves over itself; a deployed pipeline duplicates instead of saving and
// swaps Deploy for Stop/Start. The green-outline face in the mocks IS the
// library's primary button (white fill, green border and glyph — its
// Button/Primary/* variables are what the Figma buttons bind), so no
// hand-rolled confirmation button is needed.
function PipelineActions() {
  const { jobControl, saveOpenDraft, actionError, dismissActionError } =
    useStudioPipeline();
  const state = usePipelineActionState();
  const [nameModal, setNameModal] = useState<NameModalKind | null>(null);
  // The saves REJECT rather than reporting through actionError, so the
  // modal can show a failure on its own field. The draft state's bare
  // Save has no modal — its failure joins the lifecycle errors on the
  // shared toast, tagged with the draft it belongs to. That draft is the
  // only state showing the button, so the toast lives exactly as long as
  // the draft stays open: clearing, opening another pipeline or deploying
  // this one (same workspace, no longer a draft) drops it, the way the
  // provider drops actionError. (Surfaced here and not in the provider
  // precisely so the saves can go on rejecting for the modal's sake.)
  const draftSave = useDraftSave(saveOpenDraft);
  const error = resolveActionError(
    actionError,
    draftSave.saveError,
    state.openDraftId,
  );
  const dismissError = useCallback(() => {
    draftSave.dismissSaveError();
    dismissActionError();
  }, [draftSave.dismissSaveError, dismissActionError]);
  useErrorToast(error, dismissError);

  return (
    <RightGroup>
      {!state.isDeployed && (
        <DraftActions
          state={state}
          onNameModal={setNameModal}
          onSaveDraft={draftSave.saveDraft}
        />
      )}
      {state.isDeployed && jobControl && (
        <DeployedActions
          jobControl={jobControl}
          running={state.running}
          onNameModal={setNameModal}
        />
      )}
      {nameModal !== null && (
        <PipelineNameModal
          nameModal={nameModal}
          openPipeline={state.openPipeline}
          onClose={() => setNameModal(null)}
        />
      )}
      <StopAndSaveModal
        hold={draftSave.hold}
        saving={draftSave.saving}
        onConfirm={draftSave.confirmStop}
        onCancel={draftSave.cancelStop}
      />
    </RightGroup>
  );
}

// Save on a draft whose stopped job started running in the meantime:
// saving retires the job, and that needs a stop first — asked, never done
// behind the user's back.
function StopAndSaveModal({
  hold,
  saving,
  onConfirm,
  onCancel,
}: {
  hold: SaveHold | null;
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ConfirmDeleteModal
      isOpen={hold !== null}
      title="Stop the pipeline to save?"
      confirmType="primary"
      confirmLabel="Stop and save"
      loading={saving}
      loadingLabel="Stopping…"
      message={`"${hold?.jobName}" started running in the meantime. Saving keeps this pipeline as a draft and removes the running job — stop it and save?`}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

// Figma Page/title (14397-86283). The middle workspace controls live in
// WorkspaceControls (the "Open pipeline" switcher, the state badge and
// Clear); the right group holds the lifecycle actions.
function StudioHeader() {
  return (
    <PageTitleRow>
      <TitleGroup>
        <Title>Studio</Title>
      </TitleGroup>
      <WorkspaceControls />
      <PipelineActions />
      <StudioHotkeys />
      <StudioCommands />
    </PageTitleRow>
  );
}

// The workbench with its side panels. A view-only studio (a running
// pipeline, Figma 14894-157221) shows the workbench without the Explorer:
// nothing in it applies while nothing can be edited.
function WorkbenchRow() {
  const { editsLocked } = useStudioPipeline();
  const explorer = useExplorer();
  const { showAiSidebar, setShowAiSidebar } = useAiChatContext();
  const setHelperCollapsed = useCallback(
    (collapsed: boolean) => setShowAiSidebar(!collapsed),
    [setShowAiSidebar],
  );
  // The resize handles listen at the document level and hit-test by
  // coordinates, so a modal's backdrop can't block them in the DOM —
  // dragging over a gutter would resize the panels BEHIND the modal.
  const modalOpen = useAnyModalOpen();
  const layout = useDefaultLayout({ id: "studio-row", storage: userStorage });
  return (
    // biome-ignore lint/correctness/useUniqueElementIds: react-resizable-panels layout id — deliberately stable, it keys the remembered split
    <RowGroup
      id="studio-row"
      orientation="horizontal"
      disabled={modalOpen}
      {...layout}
    >
      <SidePanel
        panelId={EXPLORER_PANEL_ID}
        collapsed={explorer.collapsed}
        hidden={editsLocked}
        setCollapsed={explorer.setCollapsed}
      >
        <NewExplorer />
      </SidePanel>
      <SideSeparator $hidden={editsLocked} />
      <Panel id={MAIN_PANEL_ID} minSize={`${WORKBENCH_MIN_WIDTH}px`}>
        <StudioPanels />
      </Panel>
      <SideSeparator />
      {/* The helper stays on a running deployment — answers only, no
          Insert (useAiHelperProps drops onApplyCode). */}
      <SidePanel
        panelId={HELPER_PANEL_ID}
        collapsed={!showAiSidebar}
        setCollapsed={setHelperCollapsed}
      >
        <AiHelperPanel />
      </SidePanel>
    </RowGroup>
  );
}

// Same collapsible AI Helper as /studio, resized to the new design:
// Bridge_AIHelper (13349-30180) — full row height, flush right,
// right-rounded corners mirroring the Explorer panel on the left. Its
// width is the SidePanel's (330 at rest, up to 520).
function AiHelperPanel() {
  const { showAiSidebar, setShowAiSidebar } = useAiChatContext();
  const { sidebar, insert } = useAiHelperProps();
  // Before the early return: a completed message's explorer actions apply
  // whether or not the panel is open.
  const studioActions = useStudioActions();

  // The Replace confirm outlives the panel — an insert pending on it
  // stays answerable after a collapse.
  const replaceConfirm = (
    <ConfirmDeleteModal
      isOpen={insert.pending !== null}
      title="Replace unsaved work?"
      confirmLabel="Replace"
      confirmType="primary"
      message={
        insert.pending
          ? `Inserting into ${insert.pending.destination} replaces ${insert.pending.replaces}. That content will be lost.`
          : ""
      }
      onConfirm={insert.confirmPending}
      onCancel={insert.cancelPending}
    />
  );

  // The chat sidebar stays mounted across a collapse — it renders nothing
  // while closed but keeps its state, the attachment exclusions above all:
  // a collapse can now come from the row itself (a window too narrow for
  // the helper beside the workbench, see SidePanel), and unmounting on it
  // would silently re-include Input or Schema the user had hidden. The
  // card's chrome goes with the collapse; the strip draws its own.
  return (
    <AiPanel $collapsed={!showAiSidebar}>
      {replaceConfirm}
      {!showAiSidebar && (
        <AiCollapsedPanel
          onClick={() => setShowAiSidebar(true)}
          title="Expand AI Helper"
          data-testid="ai-panel-expand"
        >
          <AiCollapsedExpandIcon>
            <IconSVG
              icon={Icon.CollapseLeft}
              size={16}
              svgColorKey={SVGColorKey.semanticTextBody}
            />
          </AiCollapsedExpandIcon>
          <AiCollapsedLabel>AI Helper</AiCollapsedLabel>
        </AiCollapsedPanel>
      )}
      <StudioActionsContext.Provider value={studioActions}>
        <AiChatSidebar
          isOpen={showAiSidebar}
          onClose={() => setShowAiSidebar(false)}
          {...sidebar}
        />
      </StudioActionsContext.Provider>
    </AiPanel>
  );
}

const AiPanel = styled.div<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background: ${(p) => (p.$collapsed ? "transparent" : p.theme.sidebarBackground)};
  border: ${(p) => (p.$collapsed ? "none" : `1px solid ${p.theme.cards1Border}`)};
  /* The workbench's border is the shared left edge. */
  border-left: none;
  border-radius: 0 ${CARDS.corner}px ${CARDS.corner}px 0;
  overflow: hidden;

  /* Figma 13287-24452 header: fixed 50px row, centered, spacingm sides. The
     shared AiChatSidebar (also used by /studio) has a shorter header, so the
     new-design metrics are overridden here instead of in the component. */
  & > [data-testid="ai-chat-sidebar"] > div:first-child {
    height: 50px;
    box-sizing: border-box;
    padding: 0 ${SEMANTIC.spacingm}px;
  }
`;

// Mirror of the Explorer's collapsed strip, anchored on the right; fills
// the collapsed SidePanel (SIDE_PANEL_COLLAPSED_PX wide).
const AiCollapsedPanel = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: ${SEMANTIC.spacingm}px 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background: ${(p) => p.theme.sidebarBackground};
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-left: none;
  border-radius: 0 ${CARDS.corner}px ${CARDS.corner}px 0;
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.sidebarListItemHoverBackground};
  }
`;

const AiCollapsedExpandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 8px;
`;

const AiCollapsedLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${(p) => p.theme.semanticTextLight};
  writing-mode: vertical-rl;
  transform: rotate(180deg);
`;

export default function NewStudio() {
  // Arriving with a workspace to open (?workspace=<id>, from the Pipelines
  // drafts, the Pipelines page or "Open in Studio"): the studio paints its
  // code view from the first frame, the view a draft opens on.
  const [searchParams] = useSearchParams();
  const initialView = searchParams.has("workspace") ? "full" : "stages";
  return (
    <Page>
      {/* The provider covers the header too: Deploy gates on the
          pipeline-code lint status. */}
      <PipelineStagingProvider initialView={initialView}>
        {/* The pipeline lifecycle (what's open, deploy/start/stop) sits on
            top of staging — its actions read the staged editor state. */}
        <StudioPipelineProvider>
          <ExplorerProvider>
            <StudioHeader />
            <StudioSection>
              <WorkbenchRow />
            </StudioSection>
          </ExplorerProvider>
        </StudioPipelineProvider>
      </PipelineStagingProvider>
    </Page>
  );
}
